/**
 * stageProbabilities.ts — the pure decisions behind Settings → Sales's stage
 * ladder table (Phase 2 / 2.2, P2-7), kept out of the component so vitest can
 * pin them without a DOM.
 *
 * The whole point of the ladder is the distinction the wire carries: a stage
 * whose value the tenant SET (source "tenant") versus one that INHERITS the
 * platform default (source "default"). The table must show that, and the
 * save must express it: a typed number SETS a stage; "Use platform default"
 * RESETS it (a JSON null on the wire, which deletes the tenant row). The PUT
 * body carries only the stages that changed.
 */
import type { SalesStageProbability } from '../../../../api/types'

/** The pipeline labels for the four open stages (the board's vocabulary). */
export const STAGE_LABEL: Record<string, string> = {
  new_lead: 'New Lead',
  qualified: 'Qualified',
  proposal_sent: 'Proposal Sent',
  negotiation: 'Negotiation',
}

export interface LadderDraftRow {
  stage: string
  /** The input's text; '' = "use the platform default" (a reset on save). */
  value: string
  /** What the server holds today, for the diff and the labels. */
  stored: SalesStageProbability
}

/** The draft the table opens with: the stored effective ladder, as text. A
 *  stage the platform answers for still shows its number (that IS the value
 *  an unassessed deal inherits) — the source column says who set it. */
export function draftFromLadder(stages: SalesStageProbability[]): LadderDraftRow[] {
  return stages.map(s => ({ stage: s.stage, value: String(s.probability), stored: s }))
}

/** A typed value is valid when blank (reset) or an integer 0-100. */
export function ladderInputValid(v: string): boolean {
  if (v.trim() === '') return true
  const n = Number(v)
  return Number.isInteger(n) && n >= 0 && n <= 100
}

/** The one-line source copy per row, the P2-7 distinction in words. */
export function ladderSourceLabel(row: SalesStageProbability): string {
  return row.source === 'tenant'
    ? 'Set for your workspace'
    : `Inherited from the platform default`
}

/**
 * The changed-stages-only PUT body. A blank on a stage the tenant had SET
 * emits null (reset); a blank on an already-inherited stage emits nothing;
 * a number equal to the value shown emits nothing; a number that differs
 * emits the number — on a SET stage that includes typing the platform default
 * (the source stays "tenant": the tenant chose that number).
 */
export function ladderDiff(rows: LadderDraftRow[]): Record<string, number | null> {
  const body: Record<string, number | null> = {}
  for (const r of rows) {
    if (!ladderInputValid(r.value)) continue
    const typed = r.value.trim()
    if (typed === '') {
      if (r.stored.source === 'tenant') body[r.stage] = null
      continue
    }
    const n = Number(typed)
    if (r.stored.source === 'tenant' && n === r.stored.probability) continue
    if (r.stored.source === 'default' && n === r.stored.probability) continue
    body[r.stage] = n
  }
  return body
}

export function ladderSavable(rows: LadderDraftRow[], busy: boolean): boolean {
  if (busy) return false
  if (rows.some(r => !ladderInputValid(r.value))) return false
  return Object.keys(ladderDiff(rows)).length > 0
}
